export type LatencyKind = 'person' | 'edit' | 'frame';

export interface LatencySummary {
  count: number;
  p50?: number;
  p95?: number;
}

const percentile = (sorted: readonly number[], fraction: number): number | undefined => {
  if (sorted.length === 0) return undefined;
  const index = Math.min(sorted.length - 1, Math.max(0, Math.ceil(sorted.length * fraction) - 1));
  return sorted[index];
};

export class LatencyTracker {
  readonly #samples: Record<LatencyKind, number[]> = { person: [], edit: [], frame: [] };
  readonly #listeners = new Set<() => void>();

  add(kind: LatencyKind, milliseconds: number): void {
    if (!Number.isFinite(milliseconds) || milliseconds < 0 || milliseconds > 10_000) return;
    const values = this.#samples[kind];
    values.push(milliseconds);
    if (values.length > 600) values.splice(0, values.length - 600);
    for (const listener of this.#listeners) listener();
  }

  summary(kind: LatencyKind): LatencySummary {
    const sorted = [...this.#samples[kind]].sort((a, b) => a - b);
    return { count: sorted.length, p50: percentile(sorted, 0.5), p95: percentile(sorted, 0.95) };
  }

  clear(kind: LatencyKind): void {
    this.#samples[kind].length = 0;
    for (const listener of this.#listeners) listener();
  }

  subscribe(listener: () => void): () => void {
    this.#listeners.add(listener);
    return () => this.#listeners.delete(listener);
  }
}
